// =====================================================
// AnalyticsKpi.jsx
// Estate Experts — Analytics & KPI Dashboard
// Prefix: akpi-
// Charts: Recharts  |  Brand: #fff / #000
// =====================================================

import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  AreaChart, Area, BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { FiChevronDown, FiMoreHorizontal } from 'react-icons/fi';
import {
  FiEye, FiMousePointer, FiUsers, FiTarget
} from 'react-icons/fi';
import AdminMasterLayout from 'layouts/AdminMasterLayout';
import '../../../assets/css/admin/dashboard/adminanalytics/analytics.css';
import axios from 'axios';
import AppContext from 'context/AppContext';

// ─── Palette (brand-aligned) ────────────────────────────────
const COLOR = {
  views: '#7c3aed',
  clicks: '#2563eb',
  leads: '#059669',
  conversions: '#d97706',
  house: '#7c3aed',
  apartment: '#2563eb',
  plot: '#059669',
  commercial: '#d97706',
  villa: '#607fc1',
  farmHouse: '#568b7a',
  buyerBar: '#7c3aed',
  sellerBar: '#059669',
  loc: ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc3545'],
};

// ─── Static / mock data (replace with axios calls) ──────────

const sparkViews = [40, 55, 45, 70, 60, 80, 75, 90, 85, 100, 95, 110].map((v, i) => ({ v }));
const sparkClicks = [20, 30, 25, 40, 35, 55, 50, 65, 60, 75, 70, 85].map((v, i) => ({ v }));
const sparkLeads = [10, 18, 14, 22, 19, 28, 24, 32, 28, 38, 33, 42].map((v, i) => ({ v }));
const sparkConversions = [5, 8, 7, 11, 9, 14, 12, 16, 14, 18, 16, 21].map((v, i) => ({ v }));





// ─── Animated counter hook ──────────────────────────────────
function useCountUp(target, duration = 1200) {
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    // agar null/undefined aaye to 0 use karo
    const num = Number(target) || 0;

    let start = 0;
    const step = num / (duration / 16);

    const timer = setInterval(() => {
      start += step;

      if (start >= num) {
        clearInterval(timer);
        setDisplay(num.toLocaleString());
      } else {
        setDisplay(Math.floor(start).toLocaleString());
      }
    }, 16);

    return () => clearInterval(timer);
  }, [target, duration]);

  return display;
}

// ─── KPI Card ───────────────────────────────────────────────
const KpiCard = ({ label, value, change, Icon, spark, color, cardKey }) => {
  const animated = useCountUp(value);
  return (
    <div className={`akpi-kpi-card akpi-kpi-card--${cardKey}`}>
      <div className="akpi-kpi-top">
        <div className="akpi-kpi-icon-wrap">
          <Icon size={18} color={color} />
        </div>
        <p className="akpi-kpi-label" style={{ color }}>{label}</p>
        <button className="akpi-kpi-dots"><FiMoreHorizontal size={16} /></button>
      </div>

      <div className="akpi-kpi-value">{animated}</div>

      <div className={`akpi-kpi-change akpi-kpi-change--up`}>
        ↑ {change} <span style={{ fontWeight: 400, color: '#999' }}>vs last month</span>
      </div>

      <div className="akpi-kpi-chart">
        <ResponsiveContainer width="100%" height={60}>
          <AreaChart data={spark} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${cardKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.18} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone" dataKey="v"
              stroke={color} strokeWidth={2}
              fill={`url(#grad-${cardKey})`}
              dot={false} isAnimationActive={true}
              animationDuration={1200}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Filter Button ──────────────────────────────────────────
const FilterBtn = ({ label }) => (
  <button className="akpi-chart-filter">
    {label} <FiChevronDown size={12} />
  </button>
);

// ─── Custom Tooltip ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#000', color: '#fff',
      padding: '6px 12px', borderRadius: 8,
      fontSize: 12, fontWeight: 600,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
    }}>
      <div style={{ marginBottom: 2, color: '#999', fontSize: 11 }}>{label}</div>
      <div>{payload[0].value.toLocaleString()}</div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────
const AnalyticsKpi = () => {
 
  const { baseUrl ,authToken} = useContext(AppContext);
  //-----------state card ---------------//
  const [totalViews, setTotalViews] = useState("");
  const [totalClicks, setTotalClicks] = useState("");
  const [totalLeads, setTotalLeads] = useState("");
  const [totalConversions, setTotalConversions] = useState("");
  const [viewsPercentage, setViewsPercentage] = useState("");
  const [clicksPercentage, setClicksPercentage] = useState("");
  const [leadsPercentage, setLeadsPercentage] = useState("");
  const [conversionPercentage, setConversionPercentage] = useState("");
  //-----------property growth ---------------//
  const [propertyGrowthData, setPropertyGrowthData] = useState([]);
  const [growthFilter, setGrowthFilter] = useState("year");
  //-----------visit request trend ---------------//
  const [visitTrendData, setVisitTrendData] = useState([]);
  //-----------property Type Distribution ---------------//
  const [pieData, setPieData] = useState([]);
  //-----------buyer registration ---------------//
  const [buyerRegData, setBuyerRegData] = useState([]);
  //-----------seller registration ---------------//
  const [sellerRegData, setSellerRegData] = useState([]);
  //-----------Top locations ---------------//
const [topLocations, setTopLocations] = useState([]);
 const maxLoc = Math.max(
  ...topLocations.map((loc) => loc.count),
  1
);
  const totalProperties = pieData.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const KPI_CARDS = [
    {
      key: 'views',
      label: 'Views',
      value: totalViews,
      change: `${viewsPercentage}%`,
      Icon: FiEye,
      spark: sparkViews,
      color: COLOR.views,
    },
    {
      key: 'clicks',
      label: 'Clicks',
      value: totalClicks.toString(),
      change: `${clicksPercentage}%`,
      Icon: FiMousePointer,
      spark: sparkClicks,
      color: COLOR.clicks,
    },
    {
      key: 'leads',
      label: 'Leads',
      value: totalLeads.toString(),
      change: `${leadsPercentage}%`,
      Icon: FiUsers,
      spark: sparkLeads,
      color: COLOR.leads,
    },
    {
      key: 'conversions',
      label: 'Conversions',
      value: totalConversions.toString(),
      change: `${conversionPercentage}%`,
      Icon: FiTarget,
      spark: sparkConversions,
      color: COLOR.conversions,
    },
  ];
  //-----------state card api---------------//
  const viewDashboardKpi = () => {

    axios.get(`${baseUrl}/admin/analytics/viewDashboardKpi`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        console.log(response.data);
        setTotalViews(response.data.totalViews);
        setTotalClicks(response.data.totalClicks);
        setTotalLeads(response.data.totalLeads);
        setTotalConversions(response.data.totalConversions);
        setViewsPercentage(response.data.viewsPercentage);
        setClicksPercentage(response.data.clicksPercentage);
        setLeadsPercentage(response.data.leadsPercentage);
        setConversionPercentage(response.data.conversionPercentage);
      })
      .catch((error) => {
        console.log(error);
      });
  };
  //-----------property growth ---------------//
  const viewPropertyGrowth = () => {

    axios.get(`${baseUrl}/admin/analytics/propertyGrowth`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {

        console.log(response.data);

        const data = response.data.propertyGrowth.map((item) => ({
          m: item.month,
          v: item.total
        }));

        setPropertyGrowthData(data);

      })
      .catch((error) => {
        console.log(error);
      });

  };
  //-----------visit request trend api---------------//
  const viewVisitTrend = () => {

    axios.get(`${baseUrl}/admin/analytics/visitRequestTrend`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {

        console.log(response.data);

        const data = response.data.visitTrend.map((item) => ({
          d: item.month,
          v: item.total,
        }));

        setVisitTrendData(data);

      })
      .catch((error) => {
        console.log(error);
      });

  };
  //-----------property Type Distribution api---------------//
  const viewPropertyTypeDistribution = () => {

    axios
      .get(`${baseUrl}/admin/analytics/propertyTypeDistribution`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {

        console.log(response.data);

        const colors = [
          COLOR.house,
          COLOR.apartment,
          COLOR.villa,
          COLOR.plot,
          COLOR.commercial,
          COLOR.farmHouse,
        ];

        const data = response.data.propertyTypes.map((item, index) => ({

          name: item.name,
          value: item.value,
          color: colors[index]
        }));
        setPieData(data);
      })
      .catch((error) => {
        console.log(error);
      });

  }
  //-----------buyer registration api---------------//

  const viewBuyerRegistration = () => {

    axios.get(`${baseUrl}/admin/analytics/buyerRegistration`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
    .then((response)=>{

        console.log(response.data);

        const data = response.data.buyerRegistration.map((item)=>({

            m:item.month.substring(0,3),
            v:item.total

        }));

        setBuyerRegData(data);

    })
    .catch((error)=>{
        console.log(error);
    });

};
  //-----------seller registration api---------------//
const viewSellerRegistration = () => {

    axios.get(`${baseUrl}/admin/analytics/sellerRegistration`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
        .then((response) => {

            console.log(response.data);

            const data = response.data.sellerRegistration.map((item) => ({
                m: item.month.substring(0, 3),
                v: item.total,
            }));

            setSellerRegData(data);

        })
        .catch((error) => {
            console.log(error);
        });

};
  //-----------top locations api---------------//
const viewTopLocations = () => {

  axios
    .get(`${baseUrl}/admin/analytics/topLocations`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
    .then((response) => {

      console.log("Top Locations:", response.data);

      if (response.data.success) {

        const colors = [
          COLOR.loc[0],
          COLOR.loc[1],
          COLOR.loc[2],
        ];

        const data = response.data.topLocations.map((item, index) => ({
          name: item.name,
          count: Number(item.count),
          color: colors[index],
        }));

        setTopLocations(data);
      }

    })
    .catch((error) => {
      console.log("Top Locations Error:", error);
    });

};

  useEffect(() => {
    viewDashboardKpi();
    viewPropertyGrowth();
    viewVisitTrend();
    viewPropertyTypeDistribution();
    viewBuyerRegistration();
    viewSellerRegistration();
    viewTopLocations();
  }, []);

  return (
    <AdminMasterLayout pageTitle='Analytics & KPIs'>
      <div className="akpi-page">

        {/* ── Page Header ── */}
        {/* <div className="akpi-header">
          <h1 className="akpi-header-title">Analytics & KPIs</h1>
          <p className="akpi-header-sub">Track platform performance and growth metrics</p>
        </div> */}

        {/* ══════════════════════════════════════
            ROW 1 — KPI Cards
        ══════════════════════════════════════ */}
        <div className="akpi-kpi-grid">
          {KPI_CARDS.map(card => (
            <KpiCard key={card.key} {...card} cardKey={card.key} />
          ))}
        </div>

        {/* ══════════════════════════════════════
            ROW 2 — Property Growth | Visit Trend |
        ══════════════════════════════════════ */}
        <div className="akpi-charts-row">

          {/* Property Growth — Area */}
          <div className="akpi-chart-card">
            <div className="akpi-chart-head">
              <p className="akpi-chart-title">Property Growth</p>
              {/* <select
                className="akpi-chart-filter"
                value={growthFilter}
                onChange={(e) => setGrowthFilter(e.target.value)}
              >

                <option value="week">This Week</option>

                <option value="month">This Month</option>

                <option value="year">This Year</option>

              </select> */}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={propertyGrowthData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLOR.views} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={COLOR.views} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone" dataKey="v"
                  stroke={COLOR.views} strokeWidth={2.5}
                  fill="url(#gradGrowth)"
                  dot={{ r: 3, fill: COLOR.views, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                  animationDuration={1400}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Visit Trend — Line */}
          <div className="akpi-chart-card">
            <div className="akpi-chart-head">
              <p className="akpi-chart-title">Visit Requests Trend</p>
              {/* <FilterBtn label="This Month" /> */}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={visitTrendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradVisit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLOR.clicks} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={COLOR.clicks} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="d" tick={{ fontSize: 10, fill: '#767676' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone" dataKey="v"
                  stroke={COLOR.clicks} strokeWidth={2.5}
                  dot={{ r: 4, fill: '#fff', stroke: COLOR.clicks, strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                  animationDuration={1400}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>


        </div>

        {/* ══════════════════════════════════════
            ROW 2 —|Donut  | Top Locations
        ══════════════════════════════════════ */}
        <div className="akpi-middle-row">
          {/* Property Types — Donut */}
          <div className="akpi-chart-card">
            <div className="akpi-chart-head">
              <p className="akpi-chart-title">Property Types Distribution</p>
            </div>
            <div className="akpi-donut-wrap">
              <div className="akpi-donut-chart-area">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      animationBegin={200}
                      animationDuration={1200}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [`${v}%`, n]}
                      contentStyle={{ background: '#000', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                      itemStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Centre label */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  textAlign: 'center', pointerEvents: 'none'
                }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#000', display: 'block' }}>{totalProperties}</span>
                  <span style={{ fontSize: 11, color: '#767676', display: 'block' }}>Total</span>
                </div>
              </div>

              {/* Legend */}
              <div className="akpi-donut-legend">
                {pieData.map(item => (
                  <div className="akpi-donut-legend-item" key={item.name}>
                    <div className="akpi-donut-legend-left">
                      <div className="akpi-donut-legend-dot" style={{ background: item.color }} />
                      {item.name}
                    </div>
                    <span className="akpi-donut-legend-pct">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Top Locations */}
          <div className="akpi-locations-card">
            <p className="akpi-locations-title">Top Locations (By Properties)</p>
            <div className="akpi-location-row">
              {topLocations.map((loc, idx) => (
                <div className="akpi-location-item" key={loc.name}
                  style={{ animationDelay: `${0.1 + idx * 0.07}s` }}>
                  <span className="akpi-location-name">{loc.name}</span>
                  <div className="akpi-location-bar-wrap">
                    <div
                      className="akpi-location-bar"
                      style={{
                        width: `${(loc.count / maxLoc) * 100}%`,
                        background: loc.color,
                        animationDelay: `${0.3 + idx * 0.1}s`,
                      }}
                    />
                  </div>
                  <span className="akpi-location-count">{loc.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ══════════════════════════════════════
            ROW 3 — Buyer Reg | Seller Reg 
        ══════════════════════════════════════ */}
        <div className="akpi-bottom-row">

          {/* Buyer Registration — Bar */}
          <div className="akpi-chart-card">
            <div className="akpi-chart-head">
              <p className="akpi-chart-title">Buyer Registration</p>
              {/* <FilterBtn label="This Year" /> */}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={buyerRegData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="v" fill={COLOR.buyerBar} radius={[4, 4, 0, 0]} animationDuration={1200} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Seller Registration — Bar */}
          <div className="akpi-chart-card">
            <div className="akpi-chart-head">
              <p className="akpi-chart-title">Seller Registration</p>
              {/* <FilterBtn label="This Year" /> */}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sellerRegData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#767676' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="v" fill={COLOR.sellerBar} radius={[4, 4, 0, 0]} animationDuration={1200} />
              </BarChart>
            </ResponsiveContainer>
          </div>



        </div>

      </div>
    </AdminMasterLayout>
  );
};

export default AnalyticsKpi;